/**
 * "N new since <date>" for returning visitors (audit X9 / LOOP-2).
 *
 * The last visit lives in localStorage only. The count comes from
 * /api/sounds/new-since, which takes a calendar day so the response is
 * CDN-cacheable and the number of distinct URLs stays small.
 */

import { readStorage, writeStorage } from '@/lib/safe-storage'

export const LAST_VISIT_KEY = 'tls.lastVisit'
/** The result for this tab session, so a reload does not erase the line. */
export const NEW_SINCE_SESSION_KEY = 'tls.newSince.session'

/** A visitor counts as returning after a day away. */
export const MIN_AWAY_MS = 24 * 60 * 60 * 1000
/** The API only looks back this far; older visits are clamped to it. */
export const MAX_LOOKBACK_DAYS = 90

const DAY_MS = 24 * 60 * 60 * 1000

export interface NewSinceResult {
  /** UTC calendar day, YYYY-MM-DD. */
  since: string
  count: number
}

/** UTC calendar day for a timestamp. */
export function toDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/**
 * Whether the stored last visit is old enough, and recent enough to be worth a
 * count, to ask the server. Returns the day to ask about, or null.
 */
export function sinceDayForVisit(lastVisit: number | null, now: number): string | null {
  if (lastVisit === null || !Number.isFinite(lastVisit) || lastVisit > now) return null
  if (now - lastVisit < MIN_AWAY_MS) return null
  const floor = now - MAX_LOOKBACK_DAYS * DAY_MS
  return toDay(Math.max(lastVisit, floor))
}

/** "Sep 24": absolute, per the date rule in the design system. */
export function formatSinceLabel(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`)
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export function formatNewSince(result: NewSinceResult): string {
  return `${result.count.toLocaleString('en-US')} new since ${formatSinceLabel(result.since)}`
}

function readLastVisit(): number | null {
  const raw = readStorage(LAST_VISIT_KEY)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/**
 * Read the stored last visit and stamp this one. The stamp only moves forward
 * once the visitor has been away long enough to be counted, so two visits on
 * the same day still measure from the earlier one.
 */
export function takeReturningVisit(now: number): string | null {
  const last = readLastVisit()
  const day = sinceDayForVisit(last, now)
  if (last === null || day !== null) writeStorage(LAST_VISIT_KEY, String(now))
  return day
}

export function readSessionResult(): NewSinceResult | null {
  try {
    const raw = typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(NEW_SINCE_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<NewSinceResult>
    if (typeof parsed.since === 'string' && typeof parsed.count === 'number') {
      return { since: parsed.since, count: parsed.count }
    }
  } catch {
    // Storage blocked or corrupt: the line just does not survive a reload.
  }
  return null
}

export function writeSessionResult(result: NewSinceResult): void {
  try {
    sessionStorage.setItem(NEW_SINCE_SESSION_KEY, JSON.stringify(result))
  } catch {
    // See readSessionResult.
  }
}
